(function () {
  'use strict';

  if (window.SchwerHome) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sliderTeardowns = new Map();
  const accordionTeardowns = new Map();

  function initAccordion(root) {
    const buttons = [];
    if (root.matches?.('[data-schwer-accordion-button]')) buttons.push(root);
    buttons.push(...root.querySelectorAll('[data-schwer-accordion-button]'));
    buttons.forEach((button) => {
      if (button.dataset.schwerInitialized === 'true') return;
      const panelId = button.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      const expanded = button.getAttribute('aria-expanded') === 'true';
      panel.hidden = !expanded;
      button.dataset.schwerInitialized = 'true';
      const handleClick = () => {
        const nextExpanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(nextExpanded));
        panel.hidden = !nextExpanded;
      };
      button.addEventListener('click', handleClick);
      accordionTeardowns.set(button, () => {
        button.removeEventListener('click', handleClick);
        button.removeAttribute('data-schwer-initialized');
        accordionTeardowns.delete(button);
      });
    });
  }

  function unloadAccordion(root) {
    const buttons = [];
    if (root.matches?.('[data-schwer-accordion-button]')) buttons.push(root);
    buttons.push(...root.querySelectorAll('[data-schwer-accordion-button]'));
    buttons.forEach((button) => accordionTeardowns.get(button)?.());
  }

  function initSlider(slider) {
    if (slider.dataset.schwerInitialized === 'true') return;
    const track = slider.querySelector('[data-schwer-slider-track]');
    const slides = Array.from(slider.querySelectorAll('[data-schwer-slide]'));
    if (!track || slides.length === 0) return;
    slides.forEach((slide) => {
      if (!slide.hasAttribute('tabindex')) slide.setAttribute('tabindex', '-1');
    });

    let index = Math.max(0, slides.findIndex((slide) => slide.getAttribute('aria-current') === 'true'));
    let timer;
    let paused = false;
    let hovered = false;
    let focused = false;
    const delay = Math.max(3000, Number(slider.dataset.autoplayDelay) || 5000);
    const pauseButton = slider.querySelector('[data-schwer-slider-pause]');

    function syncVideos() {
      slides.forEach((slide, slideIndex) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (slideIndex === index && slider.dataset.autoplay === 'true' && !paused && !reducedMotion.matches) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    function makeAnnouncementsPolite() {
      track.setAttribute('aria-live', 'polite');
    }

    function updatePauseControl() {
      if (!pauseButton) return;
      pauseButton.setAttribute('aria-pressed', String(paused));
      pauseButton.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      pauseButton.dataset.state = paused ? 'paused' : 'playing';
      const icon = pauseButton.querySelector('[aria-hidden="true"]');
      if (icon) icon.textContent = paused ? '\u25B6' : '\u275A\u275A';
    }

    function syncReducedMotionControl() {
      if (!pauseButton) return;
      pauseButton.hidden = reducedMotion.matches;
      pauseButton.disabled = reducedMotion.matches;
      if (reducedMotion.matches) {
        pauseButton.setAttribute('aria-pressed', 'true');
        pauseButton.setAttribute('aria-label', 'Slideshow autoplay disabled by reduced motion preference');
        pauseButton.dataset.state = 'reduced-motion';
      } else {
        updatePauseControl();
      }
    }

    function show(nextIndex, focusSlide) {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.setAttribute('aria-current', String(active));
        slide.setAttribute('aria-hidden', String(!active));
      });
      track.dataset.activeSlide = String(index);
      syncVideos();
      if (focusSlide) slides[index].focus({ preventScroll: true });
    }

    function stop() {
      window.clearInterval(timer);
      timer = undefined;
    }

    function start() {
      stop();
      if (paused || hovered || focused || reducedMotion.matches || slider.dataset.autoplay !== 'true' || slides.length < 2) return;
      timer = window.setInterval(() => show(index + 1, false), delay);
    }

    const previousButton = slider.querySelector('[data-schwer-slider-previous]');
    const nextButton = slider.querySelector('[data-schwer-slider-next]');
    const handlePrevious = () => {
      paused = true;
      makeAnnouncementsPolite();
      updatePauseControl();
      show(index - 1, true);
      start();
    };
    const handleNext = () => {
      paused = true;
      makeAnnouncementsPolite();
      updatePauseControl();
      show(index + 1, true);
      start();
    };
    const handlePause = () => {
      if (reducedMotion.matches) return;
      paused = !paused;
      track.setAttribute('aria-live', paused ? 'polite' : 'off');
      updatePauseControl();
      syncVideos();
      start();
    };
    const handleReducedMotionChange = () => {
      track.setAttribute('aria-live', reducedMotion.matches || paused ? 'polite' : 'off');
      syncReducedMotionControl();
      syncVideos();
      start();
    };
    const handleBlockSelect = (event) => {
      const selectedBlockId = event.detail?.blockId ?? event.target?.closest?.('[data-block-id]')?.dataset.blockId;
      const selectedSlide = slides.find((slide) => slide.dataset.blockId === String(selectedBlockId));
      if (!selectedSlide) return;
      paused = true;
      makeAnnouncementsPolite();
      updatePauseControl();
      show(slides.indexOf(selectedSlide), false);
      stop();
    };
    const handleMouseEnter = () => {
      hovered = true;
      stop();
    };
    const handleMouseLeave = () => {
      hovered = false;
      start();
    };
    const handleFocusIn = () => {
      focused = true;
      stop();
    };
    const handleFocusOut = (event) => {
      if (event.relatedTarget && slider.contains(event.relatedTarget)) return;
      focused = false;
      start();
    };

    previousButton?.addEventListener('click', handlePrevious);
    nextButton?.addEventListener('click', handleNext);
    pauseButton?.addEventListener('click', handlePause);
    slider.addEventListener('mouseenter', handleMouseEnter);
    slider.addEventListener('mouseleave', handleMouseLeave);
    slider.addEventListener('focusin', handleFocusIn);
    slider.addEventListener('focusout', handleFocusOut);
    reducedMotion.addEventListener?.('change', handleReducedMotionChange);
    document.addEventListener('shopify:block:select', handleBlockSelect);

    slider.dataset.schwerInitialized = 'true';
    syncReducedMotionControl();
    show(index, false);
    start();

    sliderTeardowns.set(slider, () => {
      stop();
      slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
      previousButton?.removeEventListener('click', handlePrevious);
      nextButton?.removeEventListener('click', handleNext);
      pauseButton?.removeEventListener('click', handlePause);
      slider.removeEventListener('mouseenter', handleMouseEnter);
      slider.removeEventListener('mouseleave', handleMouseLeave);
      slider.removeEventListener('focusin', handleFocusIn);
      slider.removeEventListener('focusout', handleFocusOut);
      reducedMotion.removeEventListener?.('change', handleReducedMotionChange);
      document.removeEventListener('shopify:block:select', handleBlockSelect);
      slider.removeAttribute('data-schwer-initialized');
      sliderTeardowns.delete(slider);
    });
  }

  function init(root = document) {
    initAccordion(root);
    if (root.matches?.('[data-schwer-slider]')) initSlider(root);
    root.querySelectorAll('[data-schwer-slider]').forEach(initSlider);
  }

  function unload(root) {
    unloadAccordion(root);
    if (root.matches?.('[data-schwer-slider]')) sliderTeardowns.get(root)?.();
    root.querySelectorAll('[data-schwer-slider]').forEach((slider) => sliderTeardowns.get(slider)?.());
  }

  window.SchwerHome = { init, unload };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
